import dispatchIcon from "@assets/images/Dispatch.svg";
import {
  Button,
  Card,
  Container,
  Divider,
  Grid,
  TextField,
  Typography
} from "@material-ui/core";
import FormSpacer from "@saleor/components/FormSpacer";
import { SingleSelectField } from "@saleor/components/SingleSelectField/SingleSelectField";
import { useRackCreate, useRackUpdate } from "@saleor/dropOff/mutation";
import { useGetTemplateQuery } from "@saleor/dropOff/queries";
import { useTemplateStyles } from "@saleor/dropOff/styles/templates";
import { GetTemplate_template_dropOffLocations_edges_node } from "@saleor/dropOff/types/GetTemplate";
import { TemplateAction } from "@saleor/dropOff/utils/handlers";
import useNotifier from "@saleor/hooks/useNotifier";
import { maybe } from "@saleor/misc";
import { handleGraphQLErrors } from "@saleor/utils/handlers/errorHandler";
import * as React from "react";
import SVG from "react-inlinesvg";
import { useIntl } from "react-intl";

import { checkNumberOnly } from "../../utils/validation";
import DropOffRackList from "./DropOffRackList";

interface DropOffManageRacksAndBinsProps {
  nextStep?: () => void;
  onBack?: () => void;
  templateId: string;
  templateAction: TemplateAction;
}

const DropOffManageRacksAndBins: React.FC<DropOffManageRacksAndBinsProps> = props => {
  const { nextStep, onBack, templateId, templateAction } = props;

  const classes = useTemplateStyles(props);
  const intl = useIntl();
  const notify = useNotifier();

  const [dispatchRackName, setDispatchRackName] = React.useState("");
  const [prefix, setPrefix] = React.useState("");
  const [selectedHomeLocation, setSelectedHomeLocation] = React.useState(null);
  const [homeLocations, setHomeLocations] = React.useState<
    GetTemplate_template_dropOffLocations_edges_node[]
  >([]);
  const [numberOfBins, setNumberOfBins] = React.useState(
    templateAction === TemplateAction.VIEW ? 0 : 20
  );
  const [choices, setChoices] = React.useState([]);
  const [forEditRacksAndBins, setForEditRacksAndBins] = React.useState(false);
  const [selectedRack, setSelectedRack] = React.useState("");

  const editRacksAndBinsEvent = rack => {
    if (rack) {
      setDispatchRackName(rack?.name);
      setPrefix(rack?.prefix);
      setNumberOfBins(rack?.bins?.totalCount);
      setForEditRacksAndBins(true);
      setSelectedRack(rack?.id);
    }
  };

  const { data: template, refetch } = useGetTemplateQuery({
    displayLoader: true,
    skip: !templateId || templateId.length === 0,
    variables: {
      first: 100,
      id: templateId
    }
  });

  const [createRacksAndBins] = useRackCreate({
    onCompleted: e => {
      if (e.rackCreate.rackError.length === 0) {
        notify({
          status: "success",
          text: "All changes saved"
        });
        clearForm();
        refetch();
      } else {
        notify({
          status: "error",
          text: e.rackCreate.rackError[0].message
        });
      }
    },
    onError: handleGraphQLErrors
  });

  const [updateRacksAndBins] = useRackUpdate({
    onCompleted: e => {
      if (e.rackUpdate.rackError.length === 0) {
        notify({
          status: "success",
          text: "All changes saved"
        });
        clearForm();
        refetch();
      } else {
        notify({
          status: "error",
          text: e.rackUpdate.rackError[0].message
        });
      }
    },
    onError: handleGraphQLErrors
  });

  const clickSaveChanges = () => {
    if (!selectedHomeLocation) {
      notify({
        status: "error",
        text: "Select Home Location to proceed."
      });
      return;
    }

    if (!numberOfBins || numberOfBins <= 0) {
      notify({
        status: "error",
        text: "Number of bins must be greater than 0"
      });
      return;
    }

    createRacksAndBins({
      variables: {
        input: {
          dropOffLocationId: selectedHomeLocation,
          name: dispatchRackName,
          prefix,
          templateId,
          totalBin: numberOfBins
        }
      }
    });
    clearForm();
  };

  const clickUpdateChanges = () => {
    updateRacksAndBins({
      variables: {
        id: selectedRack,
        input: {
          dropOffLocationId: selectedHomeLocation,
          name: dispatchRackName,
          prefix,
          templateId,
          totalBin: numberOfBins
        }
      }
    });
  };

  const clearForm = () => {
    setSelectedHomeLocation(null);
    setDispatchRackName("");
    setPrefix("");
    setNumberOfBins(0);
    setForEditRacksAndBins(false);
    setSelectedRack("");
  };

  React.useEffect(() => {
    if (template?.template?.dropOffLocations?.edges) {
      const homelocs = template?.template?.dropOffLocations?.edges.map(
        edge => edge.node
      );
      setHomeLocations(homelocs);
    }
  }, [template?.template?.dropOffLocations]);

  React.useEffect(() => {
    const choices = homeLocations?.map(e => ({
      label: e.name,
      value: e.id
    }));
    setChoices(choices);
  }, [homeLocations]);

  const getTitle = () => {
    if (templateAction === TemplateAction.CREATE) {
      return "Add Dispatch Racks and Bins";
    } else if (templateAction === TemplateAction.EDIT) {
      return "Edit Dispatch Racks and Bins";
    } else {
      return "View Dispatch Racks and Bins";
    }
  };

  return (
    <Container>
      <h2 className={classes.displayFlexDispatchIcon}>
        <SVG src={dispatchIcon} />
        <span className={classes.textMarginLeft}>Dispatch Area</span>
      </h2>
      <Card className={classes.dropOffCardAccordion}>
        <div className={classes.dropOffCardAccordionBody}>
          <Grid container spacing={2}>
            <Grid item xs={10}>
              <Typography
                variant="h5"
                className={classes.dropOffCardAccordionTitle}
              >
                {getTitle()}
              </Typography>
            </Grid>
          </Grid>

          <FormSpacer spacing={2} />
          <Divider variant="fullWidth" />
          <FormSpacer spacing={2} />

          {
            <>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <SingleSelectField
                    name={"selectedHomeLocation"}
                    value={selectedHomeLocation}
                    selectProps={{
                      fullWidth: true
                    }}
                    label="Home Location"
                    choices={choices}
                    onChange={e => {
                      // eslint-disable-next-line no-console
                      console.log(e.target.value);
                      clearForm();
                      setSelectedHomeLocation(e.target.value);
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    disabled={templateAction === TemplateAction.VIEW}
                    required
                    value={dispatchRackName}
                    label={intl.formatMessage({
                      defaultMessage: "Dispatch Rack Name"
                    })}
                    fullWidth
                    onChange={event => {
                      setDispatchRackName(event.target.value);
                    }}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    disabled={templateAction === TemplateAction.VIEW}
                    required
                    value={prefix}
                    label={intl.formatMessage({
                      defaultMessage: "Prefix"
                    })}
                    fullWidth
                    onChange={event => {
                      if (event.target.value.length > 1) {
                        return;
                      }
                      setPrefix(event.target.value);
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    disabled={templateAction === TemplateAction.VIEW}
                    required
                    value={numberOfBins}
                    label={intl.formatMessage({
                      defaultMessage: "Number of Bins"
                    })}
                    fullWidth
                    onKeyDown={checkNumberOnly}
                    onChange={event => {
                      if (parseInt(event.target.value, 10) > 100) {
                        return;
                      }
                      setNumberOfBins(parseInt(event.target.value, 10));
                    }}
                    type={"Number"}
                  />
                </Grid>
                {templateAction !== TemplateAction.VIEW ? (
                  <>
                    <Button
                      className={classes.buttonColorGray}
                      variant="outlined"
                      style={{
                        marginLeft: "auto"
                      }}
                      onClick={() => clearForm()}
                    >
                      <span>CANCEL</span>
                    </Button>
                    <>
                      {maybe(() => forEditRacksAndBins) ? (
                        <Button
                          className={classes.buttonColorYellow}
                          variant="contained"
                          color="primary"
                          style={{
                            marginLeft: 15,
                            marginRight: 10
                          }}
                          onClick={clickUpdateChanges}
                        >
                          <span>SAVE CHANGES</span>
                        </Button>
                      ) : (
                        <Button
                          className={classes.buttonColorYellow}
                          variant="contained"
                          color="primary"
                          style={{
                            marginLeft: 15,
                            marginRight: 10
                          }}
                          onClick={clickSaveChanges}
                        >
                          <span>ADD</span>
                        </Button>
                      )}
                    </>
                  </>
                ) : (
                  <></>
                )}
              </Grid>
            </>
          }
        </div>
      </Card>

      <FormSpacer spacing={2} />
      {selectedHomeLocation ? (
        <DropOffRackList
          templateAction={templateAction}
          clearForm={clearForm}
          templateId={templateId}
          selectedHomeLocation={selectedHomeLocation}
          editRacksAndBinsEvent={editRacksAndBinsEvent}
          selectedRackId={selectedRack}
        />
      ) : (
        <></>
      )}

      <div
        className={`${classes.displayFlex} ${classes.displayFlexPaddingTop50}`}
      >
        <Button
          color="primary"
          variant="outlined"
          className={classes.displayFlexMarginLeft}
          onClick={() => onBack()}
        >
          <div className={classes.textBackButton}>BACK</div>
        </Button>

        <Button
          variant="contained"
          className={`${classes.buttonColor} ${classes.displayFlexMarginLeft20}`}
          onClick={() => {
            if (template?.template?.racks?.totalCount <= 0) {
              notify({
                status: "error",
                text:
                  "At least 1 Dispatch Rack per Home Location must be created."
              });
              return;
            }
            nextStep();
          }}
        >
          <div className={classes.textButton}>NEXT</div>
        </Button>
      </div>
    </Container>
  );
};

export default DropOffManageRacksAndBins;
